import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2, Sparkles } from "lucide-react";
import HrefLangTags from "../components/HrefLangTags";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { useLanguage } from "../contexts/LanguageContext";
import { useCurrency } from "../contexts/CurrencyContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Each row maps to an i18n key suffix that lives under `services_compare.<row>`.
const COMPARE_ROWS = [
  { key: "what",     label_k: "services_compare.what" },
  { key: "duration", label_k: "services_compare.duration" },
  { key: "deliver",  label_k: "services_compare.deliver" },
  { key: "outcome",  label_k: "services_compare.outcome" },
  { key: "ideal",    label_k: "services_compare.ideal" },
];

// Per-service cell content keys (under services_compare.<slug>.<row>).
const SERVICE_ORDER = [
  { slug: "meta-ads",        tone: "from-brand-pink to-brand-magenta",     icon: "🎯" },
  { slug: "google-ads",      tone: "from-blue-500 to-cyan-500",            icon: "🔎" },
  { slug: "seo",             tone: "from-emerald-500 to-teal-500",         icon: "📈" },
  { slug: "creative",        tone: "from-postun-yellow to-amber-400",      icon: "✨" },
  { slug: "social-media",    tone: "from-purple-500 to-pink-500",          icon: "📱" },
  { slug: "web-development", tone: "from-indigo-600 to-blue-500",          icon: "💻" },
];

export default function ServicesIndexPage() {
  const { t, lang } = useLanguage();
  const { format: formatPrice } = useCurrency();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/services`, { params: { lang } })
      .then((r) => setServices(Array.isArray(r.data) ? r.data : []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, [lang]);

  const byslug = Object.fromEntries(services.map((s) => [s.slug, s]));
  const startingPriceFor = (svc) => {
    const tiers = svc?.pricing || [];
    if (!tiers.length) return null;
    return Math.min(...tiers.map((t) => t.price_try).filter(Boolean));
  };

  const pageTitle = `${t("services_compare.page_title", "Hizmetlerimiz")} · Yastık Medya`;

  return (
    <div data-testid="services-index-page" className="min-h-screen bg-cream text-ink">
      <SeoHead title={pageTitle} description={t("services_compare.meta_desc", "Yastık Medya hizmetlerini yan yana karşılaştır. Markana uygun olanı seç.")} path={`/${lang}/services`} imageKey="services" lang={lang} />
      <HrefLangTags pathSuffix="services" />
      <Header />

      <main>
        {/* Hero */}
        <section className="py-section sm:py-section-lg">
          <div className="container-page">
            <Reveal as="div" className="max-w-3xl">
              <span className="eyebrow"><Sparkles className="h-3 w-3 text-brand-pink" />
                {t("services_compare.eyebrow", "HİZMETLERİMİZ")}
              </span>
              <h1 className="mt-5 font-display text-display-1 font-bold leading-[0.98] text-ink">
                {t("services_compare.title_pre", "Hangi hizmet")}{" "}
                <span className="gold-foil-text">{t("services_compare.title_accent", "markana uygun?")}</span>
              </h1>
              <p className="mt-6 text-body-lg text-ink/65 leading-relaxed max-w-xl">
                {t("services_compare.subtitle", "4 ana hizmetimizi yan yana karşılaştır. Her birinin ne yaptığını, kime uygun olduğunu ve başlangıç fiyatını bir bakışta gör.")}
              </p>
            </Reveal>
          </div>
        </section>

        {/* Compare table — desktop grid, stacked mobile cards */}
        <section className="pb-section sm:pb-section-lg">
          <div className="container-page">
            {loading ? (
              <div className="text-center py-20 text-ink/55"><Loader2 className="h-5 w-5 animate-spin inline" /> {t("loading", "Yükleniyor…")}</div>
            ) : (
              <>
                {/* Desktop: 6-column grid (3 cols x 2 rows for readability) */}
                <div className="hidden lg:grid grid-cols-3 gap-5">
                  {SERVICE_ORDER.map((s, i) => {
                    const svc = byslug[s.slug];
                    const startPrice = startingPriceFor(svc);
                    return (
                      <Reveal key={s.slug} as="div" delay={i * 0.06}
                        className="rounded-3xl bg-white border border-mist overflow-hidden flex flex-col"
                        data-testid={`compare-card-${s.slug}`}>
                        <div className={`bg-gradient-to-br ${s.tone} text-cream p-6`}>
                          <div className="text-3xl mb-2">{s.icon}</div>
                          <h3 className="font-display text-xl font-bold leading-tight">
                            {t(`services_options.${s.slug.replace(/-/g, "_")}`, s.slug)}
                          </h3>
                          {startPrice ? (
                            <p className="mt-2 text-xs uppercase tracking-[0.18em] font-semibold opacity-90">
                              {t("services_compare.starts_from", "başlangıç")} {formatPrice(startPrice)}/ay
                            </p>
                          ) : null}
                        </div>

                        <div className="p-5 flex-1 space-y-4 text-sm text-ink/75">
                          {COMPARE_ROWS.map((row) => (
                            <div key={row.key}>
                              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/45 mb-1">
                                {t(row.label_k, row.key)}
                              </p>
                              <p className="leading-relaxed">
                                {t(`services_compare.${s.slug.replace(/-/g, "_")}.${row.key}`, "—")}
                              </p>
                            </div>
                          ))}
                        </div>

                        <Link to={`/${lang}/services/${s.slug}`}
                          className="mx-5 mb-5 mt-2 btn-outline-ink !text-xs !py-2.5 inline-flex items-center justify-center gap-2"
                          data-testid={`compare-detail-${s.slug}`}>
                          {t("services_compare.detail", "Detayı Gör")}
                          <ArrowUpRight className="h-3.5 w-3.5" />
                        </Link>
                      </Reveal>
                    );
                  })}
                </div>

                {/* Mobile: stacked cards */}
                <div className="lg:hidden space-y-5">
                  {SERVICE_ORDER.map((s) => {
                    const svc = byslug[s.slug];
                    const startPrice = startingPriceFor(svc);
                    return (
                      <div key={s.slug} className="rounded-3xl bg-white border border-mist overflow-hidden"
                        data-testid={`compare-card-mobile-${s.slug}`}>
                        <div className={`bg-gradient-to-br ${s.tone} text-cream p-5 flex items-start gap-4`}>
                          <div className="text-3xl">{s.icon}</div>
                          <div>
                            <h3 className="font-display text-lg font-bold leading-tight">
                              {t(`services_options.${s.slug.replace(/-/g, "_")}`, s.slug)}
                            </h3>
                            {startPrice ? (
                              <p className="mt-1 text-xs opacity-90">
                                {t("services_compare.starts_from", "başlangıç")} {formatPrice(startPrice)}/ay
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="p-5 space-y-3">
                          {COMPARE_ROWS.map((row) => (
                            <div key={row.key} className="flex gap-3">
                              <Check className="h-4 w-4 text-brand-pink shrink-0 mt-0.5" />
                              <div>
                                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/55">
                                  {t(row.label_k, row.key)}
                                </p>
                                <p className="text-sm text-ink/75">
                                  {t(`services_compare.${s.slug.replace(/-/g, "_")}.${row.key}`, "—")}
                                </p>
                              </div>
                            </div>
                          ))}
                          <Link to={`/${lang}/services/${s.slug}`}
                            className="btn-outline-ink w-full !text-xs !py-2.5 inline-flex items-center justify-center gap-2 mt-3"
                            data-testid={`compare-detail-mobile-${s.slug}`}>
                            {t("services_compare.detail", "Detayı Gör")} <ArrowUpRight className="h-3.5 w-3.5" />
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
